import { useEffect, useRef, useState } from 'react'

interface MultiSelectDropdownProps<T extends string> {
  id: string
  /** Accessible name of the option group. */
  ariaLabel: string
  options: readonly T[]
  label: (option: T) => string
  selected: readonly T[]
  /** Options that can't be picked (shown as "subscribed"-style, greyed out). */
  disabledOptions?: ReadonlySet<T>
  /** Fallback note for disabled options. */
  disabledNote?: string
  /** Per-option note overriding {@code disabledNote} (why this particular option is disabled). */
  disabledNotes?: ReadonlyMap<T, string>
  placeholder?: string
  /** Whole control inert (e.g. while a request is in flight). */
  disabled?: boolean
  onChange: (next: T[]) => void
}

/**
 * Checkbox dropdown. Closes on outside click, Escape and when focus leaves it.
 *
 * Browsers disagree about what a press inside the panel focuses: Safari focuses neither buttons
 * nor checkboxes, and a scrollbar press focuses whatever the engine decides. Closing on "focus
 * left the control" would therefore close the panel in the middle of a legitimate in-panel press.
 * So the close-on-blur rule does not depend on where focus lands: any press that STARTS inside the
 * panel is tracked (pressInsideRef) and blur-closing is suspended until the button is released.
 * Presses on the items additionally cancel the focus change (preventDefault), so keyboard focus
 * stays on the trigger; a press on the panel's own box is its scrollbar and keeps its default so
 * the thumb can be dragged.
 */
export function MultiSelectDropdown<T extends string>({
  id,
  ariaLabel,
  options,
  label,
  selected,
  disabledOptions,
  disabledNote = 'unavailable',
  disabledNotes,
  placeholder = 'Select…',
  disabled,
  onChange,
}: MultiSelectDropdownProps<T>) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const pressInsideRef = useRef(false)
  const panelId = `${id}-panel`
  const isDisabled = (o: T) => disabledOptions?.has(o) ?? false

  // The control goes inert while busy: close it so nothing can be toggled meanwhile.
  useEffect(() => { if (disabled) setOpen(false) }, [disabled])

  useEffect(() => {
    if (!open) {
      pressInsideRef.current = false
      return
    }
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        pressInsideRef.current = false
        setOpen(false)
      }
    }
    const onUp = () => {
      if (!pressInsideRef.current) return
      pressInsideRef.current = false
      // Whatever the engine focused during the press, hand focus back to the trigger so keyboard
      // use continues from there and focus is never stranded on <body> or the panel.
      if (document.activeElement !== triggerRef.current) triggerRef.current?.focus()
    }
    const onKey = (e: KeyboardEvent) => {
      // Safety net for a press whose mouseup never reached the page (button released outside the
      // window): the flag must not outlive it, or Tab-out could never close the panel again.
      // Known, accepted edge: keyboard and mouse state are independent, so a key pressed WHILE a
      // mouse button is still held inside the panel also clears the flag, and a focus move caused
      // by that key can close the panel and drop that one toggle (the user just clicks again).
      // A mechanism with no such edge (Pointer Events + pointer capture) was judged not worth the
      // untestable native-scrollbar risk in Firefox/WebKit.
      pressInsideRef.current = false
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    // Window loses focus (alt-tab, release over another app): the press is over, whatever happened.
    const onWindowBlur = () => { pressInsideRef.current = false }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('mouseup', onUp)
    document.addEventListener('keydown', onKey)
    window.addEventListener('blur', onWindowBlur)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('mouseup', onUp)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('blur', onWindowBlur)
    }
  }, [open])

  const selectable = options.filter(o => !isDisabled(o))
  const toggle = (o: T) =>
    onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o])
  const summary = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? label(selected[0])
      : `${selected.length} selected`

  return (
    <div
      ref={rootRef}
      className="relative"
      // Tabbing out closes the panel (mousedown only covers the pointer) — but never in the middle
      // of a press that started inside it (see the class comment).
      onBlur={e => {
        if (pressInsideRef.current) return
        if (open && !rootRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center justify-between gap-2 bg-surface border border-surface-card rounded-lg px-3 py-2 text-sm text-ink text-left focus:outline-none focus:border-accent-blue disabled:opacity-50"
      >
        <span className="truncate">{summary}</span>
        <span aria-hidden className="text-ink-muted text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div
          id={panelId}
          role="group"
          aria-label={ariaLabel}
          tabIndex={-1}
          onMouseDownCapture={() => { pressInsideRef.current = true }}
          onMouseDown={e => { if (e.target !== e.currentTarget) e.preventDefault() }}
          className="absolute z-20 mt-1 w-full min-w-[14rem] max-h-72 overflow-y-auto bg-surface border border-surface-card rounded-lg shadow-lg py-1"
        >
          <div className="flex gap-3 px-3 py-1.5 text-xs border-b border-surface-card">
            <button type="button" disabled={disabled} className="text-accent-blue hover:underline disabled:opacity-50" onClick={() => onChange([...selectable])}>
              Select all
            </button>
            <button type="button" disabled={disabled} className="text-ink-muted hover:underline disabled:opacity-50" onClick={() => onChange([])}>
              Clear
            </button>
          </div>
          {options.map(o => {
            const taken = isDisabled(o)
            return (
              <label
                key={o}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm ${taken ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-card'}`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  disabled={disabled || taken}
                  onChange={() => toggle(o)}
                />
                <span className="text-ink">{label(o)}</span>
                {taken && <span className="ml-auto text-xs text-ink-muted">{disabledNotes?.get(o) ?? disabledNote}</span>}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
